import { APP_THEME_LOCKED, APP_THEME_MODE } from '../confs/app';
import { useEffect } from 'react';
import { combine, persist } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

export type Theme = 'light' | 'dark';
export type ThemeMode = 'auto' | Theme;

export const getSystemTheme = (): Theme | undefined => {
  if (typeof window == 'undefined') return undefined;
  const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  const isLight = window.matchMedia('(prefers-color-scheme: light)').matches;
  return isDark ? 'dark' : isLight ? 'light' : undefined;
};

export const getClassTheme = (): Theme | undefined => {
  if (typeof window == 'undefined') return undefined;
  const classList = window.document.documentElement.classList;
  const isDark = classList.contains('dark');
  const isLight = classList.contains('light');
  return isDark ? 'dark' : isLight ? 'light' : undefined;
};

export const setClassTheme = (theme: Theme) => {
  if (typeof window == 'undefined') return;
  const classList = window.document.documentElement.classList;
  classList.remove('dark', 'light');
  classList.add(theme);
};

// save key
export const ThemeSaveKey = 'app-theme';

export const useThemeStore = createWithEqualityFn(
  persist(
    combine(
      {
        theme:
          APP_THEME_LOCKED ?? (APP_THEME_MODE === 'auto' ? getSystemTheme() || getClassTheme() || 'light' : APP_THEME_MODE),
        themeMode: (APP_THEME_LOCKED ?? APP_THEME_MODE) as ThemeMode,
      },
      set => ({
        setTheme(theme: Theme | undefined) {
          set({ theme: APP_THEME_LOCKED ?? theme });
        },
        setThemeMode(themeMode: ThemeMode) {
          if (APP_THEME_LOCKED) return;
          const tmpTheme = themeMode === 'auto' ? getSystemTheme() || getClassTheme() : themeMode;
          if (tmpTheme) setClassTheme(tmpTheme);
          set({ themeMode, theme: tmpTheme });
        },
      })
    ),
    {
      name: ThemeSaveKey,
      // While locked, ignore the persisted user choice at rehydrate but leave
      // the stored entry in place — unlocking restores the user's pick.
      merge: (persisted, current) =>
        APP_THEME_LOCKED ? current : { ...current, ...(persisted as Partial<typeof current>) },
    }
  ),
  shallow
);

export const useTheme = () => {
  const { theme, themeMode, setTheme, setThemeMode } = useThemeStore(state => state);
  useEffect(() => {
    setClassTheme(theme);
  }, [theme]);
  useEffect(() => {
    const handle = () => {
      if (themeMode === 'auto') {
        setTheme(getSystemTheme() || 'light');
      }
    };
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', handle);
    return () => {
      mediaQuery.removeEventListener('change', handle);
    };
  }, [setTheme, themeMode]);
  return { theme, themeMode, setTheme, setThemeMode };
};
